import { Link } from 'react-router-dom';
import SvgAsset from '../../SvgAsset';
import { FeedbackMessage } from '../FeedbackMessage';
import { EmailField, MfaFields, PasswordField, SubmitButton } from '../fields';
import type { AuthTemplateProps } from '../types';

export default function LoginTemplate(props: AuthTemplateProps) {
    const hasOauthProviders = props.oauthProviders.length > 0;

    return (
        <section className="mx-auto max-w-lg space-y-4">
            <div className="p-8 bg-coconut rounded-lg space-y-5">
                <h1 className="text-2xl font-semibold">Log in</h1>
                <p className="text-base text-licorice">
                    Use your Insureworks account to continue to {props.destination}.
                </p>

                <FeedbackMessage feedback={props.message} />
                <FeedbackMessage feedback={props.error} tone="error" />

                {props.verificationPending ? (
                    <div
                        className={`space-y-3.5 p-5 rounded-lg ${
                            props.resendMessage?.type === 'success'
                                ? 'bg-avocado/10 text-matcha'
                                : 'bg-blueberry/10 text-blackberry'
                        }`}
                    >
                        <p className="font-semibold">Verify your email to continue</p>
                        <p className="text-sm">
                            Check your inbox for a verification link. You can resend it below if you
                            need a fresh link.
                        </p>
                        <div className="flex items-center gap-3 flex-wrap">
                            {props.resendMessage ? (
                                <FeedbackMessage feedback={props.resendMessage} className="mt-0" />
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-xs btn-solid-blueberry"
                                    onClick={props.onResendVerification}
                                    disabled={props.loading || props.resendLoading}
                                >
                                    {props.resendLoading
                                        ? 'Resending...'
                                        : 'Resend verification email'}
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <form className="space-y-4" onSubmit={props.onSubmit} noValidate>
                        {!props.oauthMfaPending && (
                            <>
                                {hasOauthProviders && (
                                    <div className="space-y-2">
                                        {props.oauthProviders.map((provider) => (
                                            <a
                                                key={provider.id}
                                                className="btn btn-sm btn-outline-licorice w-full"
                                                href={props.oauthStartHref(provider.id, 'login')}
                                            >
                                                {provider.logo && (
                                                    <SvgAsset
                                                        className="w-5 h-auto mr-3.5"
                                                        src={provider.logo}
                                                    />
                                                )}
                                                Continue with {provider.label}
                                            </a>
                                        ))}
                                    </div>
                                )}

                                {hasOauthProviders && (
                                    <div className="flex items-center gap-3.5 my-6 -mx-8 text-xs font-bold uppercase tracking-wide text-oyster">
                                        <span className="flex-1 border-b-2 border-marshmallow" />
                                        <span className="flex-none">or</span>
                                        <span className="flex-1 border-b-2 border-marshmallow" />
                                    </div>
                                )}

                                <EmailField {...props} />
                                <PasswordField
                                    {...props}
                                    label="Password"
                                    autoComplete="current-password"
                                />
                            </>
                        )}

                        {props.mfaRequired && <MfaFields {...props} />}

                        {!props.oauthMfaPending && (
                            <div className="flex items-center justify-between">
                                <Link
                                    className="text-sm font-semibold underline decoration-dotted hover:no-underline"
                                    to={props.authHref('forgot-password')}
                                >
                                    Forgot password?
                                </Link>

                                {props.signupEnabled && (
                                    <Link
                                        className="text-sm font-semibold underline decoration-dotted hover:no-underline"
                                        to={props.authHref('signup')}
                                    >
                                        Create a new account
                                    </Link>
                                )}
                            </div>
                        )}

                        <SubmitButton
                            label={props.oauthMfaPending ? 'Continue' : 'Log in'}
                            loading={props.loading}
                        />
                    </form>
                )}
            </div>
        </section>
    );
}
